import React, { useMemo, useState } from 'react';
import { Button, Card, Flex, Input, List, Modal, Space, Typography, message, Row, Col } from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined, EditOutlined, EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { AssociationConfig, AssociationItem } from '../types';
import { createEmptyAssociationConfig } from '../types';
import './AssociationLessonAdmin.css';
import { useObjectsApi } from '../../../api/objects/objects';
import type { TImage } from '../../../models/objects/media';
import JsonEditor from '../../JsonEditor/JsonEditor';

const { Title, Text } = Typography;

const LANGUAGES: Array<'en' | 'fr' | 'ar'> = ['en', 'fr', 'ar'];

type Props = {
  config: AssociationConfig;
  onConfigChange: (config: AssociationConfig) => void;
  onSave: () => void;
  saving?: boolean;
  initialStatement?: string;
  onStatementChange?: (value: string) => void;
};

const createId = () => `assoc-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const AssociationLessonAdmin: React.FC<Props> = ({ config, onConfigChange, onSave, saving, initialStatement, onStatementChange }) => {
  const { t, i18n } = useTranslation();
  const [previewMode, setPreviewMode] = useState<'left' | 'right' | null>(null);
  const objectsApi = useObjectsApi();
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [mediaPickerSide, setMediaPickerSide] = useState<'left' | 'right' | null>(null);
  const [mediaPickerItemId, setMediaPickerItemId] = useState<string | null>(null);
  const [images, setImages] = useState<TImage[]>([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaSearch, setMediaSearch] = useState<string>('');
  const filteredImages = useMemo(() => {
    const q = mediaSearch.trim().toLowerCase();
    if (!q) return images;
    return images.filter((img) =>
      (img.file_name || '').toLowerCase().includes(q) || (img.file_path || '').toLowerCase().includes(q)
    );
  }, [images, mediaSearch]);
  const [jsonOpen, setJsonOpen] = useState(false);

  const safeConfig = useMemo(
    () => ({ ...createEmptyAssociationConfig(), ...config, left: config.left || [], right: config.right || [] }),
    [config],
  );

  const updateConfig = (updater: (prev: AssociationConfig) => AssociationConfig) => {
    const next = updater(safeConfig);
    onConfigChange(next);
  };

  const addItem = (side: 'left' | 'right') => {
    const item: AssociationItem = { id: createId(), image: '', matchKey: '' };
    updateConfig((prev) => ({ ...prev, [side]: [...(side === 'left' ? prev.left : prev.right), item] }));
  };

  const updateItem = (side: 'left' | 'right', id: string, patch: Partial<AssociationItem>) => {
    updateConfig((prev) => ({
      ...prev,
      [side]: (side === 'left' ? prev.left : prev.right).map((it) => (it.id === id ? { ...it, ...patch } : it)),
    }));
  };

  const removeItem = (side: 'left' | 'right', id: string) => {
    Modal.confirm({
      title: t('associationLesson.admin.confirmDelete') || 'Supprimer cet élément ?',
      onOk: () =>
        updateConfig((prev) => ({
          ...prev,
          [side]: (side === 'left' ? prev.left : prev.right).filter((it) => it.id !== id),
        })),
    });
  };

  const handleSave = () => {
    onSave();
  };

  const openMediaPicker = async (side: 'left' | 'right', id: string) => {
    setMediaPickerSide(side);
    setMediaPickerItemId(id);
    setMediaPickerOpen(true);
    setMediaLoading(true);
    try {
      const resp = await objectsApi.getImages();
      setImages(resp.data || []);
    } catch (e: any) {
      message.error(e?.response?.data || e?.message || t('associationLesson.admin.errorLoadingMedia') || 'Erreur lors du chargement des médias');
    } finally {
      setMediaLoading(false);
    }
  };

  const chooseMedia = (path: string) => {
    if (!mediaPickerSide || !mediaPickerItemId) return;
    updateItem(mediaPickerSide, mediaPickerItemId, { image: path });
    setMediaPickerOpen(false);
    setMediaPickerSide(null);
    setMediaPickerItemId(null);
  };

  const listRenderer = (side: 'left' | 'right') => {
    const items = side === 'left' ? safeConfig.left : safeConfig.right;
    // Get title by current language
    const lang = i18n.language || 'en';
    const langKey = lang.startsWith('fr') ? 'fr' : lang.startsWith('ar') ? 'ar' : 'en';
    const titleKey = `${side}Title_${langKey}` as keyof AssociationConfig;
    const title = (safeConfig[titleKey] as string) || 
      (side === 'left' 
        ? t('associationLesson.admin.leftTitle') || 'Colonne gauche'
        : t('associationLesson.admin.rightTitle') || 'Colonne droite');

    return (
      <Card
        title={title}
        extra={
          <Space>
            <Button icon={previewMode === side ? <EyeInvisibleOutlined /> : <EyeOutlined />} onClick={() => setPreviewMode(previewMode === side ? null : side)}>
              {previewMode === side
                ? t('associationLesson.admin.hidePreview') || 'Masquer les id'
                : t('associationLesson.admin.showPreview') || 'Afficher les id'}
            </Button>
            <Button icon={<PlusOutlined />} onClick={() => addItem(side)}>
              {t('associationLesson.admin.addItem') || 'Ajouter'}
            </Button>
          </Space>
        }
      >
        <List
          dataSource={items}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Button icon={<DeleteOutlined />} danger onClick={() => removeItem(side, item.id)} />,
              ]}
            >
              <Flex vertical gap={12} style={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    {t('associationLesson.admin.label') || 'Libellé'}
                  </Text>
                  <Row gutter={[8, 8]}>
                    {LANGUAGES.map((lang) => (
                      <Col span={8} key={lang}>
                        <Input
                          addonBefore={lang.toUpperCase()}
                          value={item[`text_${lang}` as keyof AssociationItem] as string || ''}
                          onChange={(e) => updateItem(side, item.id, { [`text_${lang}`]: e.target.value } as Partial<AssociationItem>)}
                          placeholder={t('associationLesson.admin.labelPlaceholder', { lang: lang.toUpperCase() }) || `Label ${lang.toUpperCase()}...`}
                        />
                      </Col>
                    ))}
                  </Row>
                </div>
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    addonBefore={t('associationLesson.admin.imageUrl') || "Image"}
                    value={item.image}
                    readOnly
                    placeholder={t('associationLesson.admin.imagePlaceholder') || 'Sélectionner depuis la médiathèque'}
                  />
                  <Button onClick={() => openMediaPicker(side, item.id)}>
                    {t('associationLesson.admin.chooseImage') || 'Choisir image'}
                  </Button>
                  {item.image && (
                    <Button danger onClick={() => updateItem(side, item.id, { image: '' })}>
                      {t('common.clear') || 'Effacer'}
                    </Button>
                  )}
                </Space.Compact>
                {!!item.image && (
                  <div style={{ paddingTop: 8, display: 'flex', justifyContent: 'center' }}>
                    <img 
                      src={item.image} 
                      alt={item.text_en || item.text_fr || item.text_ar || item.id} 
                      className="association-image-preview" 
                    />
                  </div>
                )}
                <Input
                  addonBefore={t('associationLesson.admin.matchKey') || "Clé d'association"}
                  value={item.matchKey}
                  onChange={(e) => updateItem(side, item.id, { matchKey: e.target.value })}
                  suffix={previewMode === side ? item.id : undefined}
                />
              </Flex>
            </List.Item>
          )}
        />
      </Card>
    );
  };

  return (
    <div className="association-admin">
      <Card
        title={<Title level={4} style={{ margin: 0 }}>{t('associationLesson.admin.title') || "Leçon d'association"}</Title>}
        extra={
          <Space>
            <Button icon={<EditOutlined />} onClick={() => setJsonOpen(true)}>
              {t('associationLesson.admin.openJsonEditor') || 'Éditeur JSON'}
            </Button>
            <Button icon={<EditOutlined />} onClick={() => updateConfig(() => createEmptyAssociationConfig())}>
              {t('associationLesson.admin.reset') || 'Réinitialiser'}
            </Button>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
              {t('associationLesson.admin.save') || 'Sauvegarder'}
            </Button>
          </Space>
        }
      >
        <Flex vertical gap={16}>
          <Input.TextArea
            value={initialStatement ?? safeConfig.statement}
            onChange={(e) => {
              const value = e.target.value;
              updateConfig((prev) => ({ ...prev, statement: value }));
              onStatementChange?.(value);
            }}
            autoSize={{ minRows: 3, maxRows: 8 }}
            placeholder={t('associationLesson.admin.statement') || 'Énoncé'}
          />
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              {t('associationLesson.admin.leftTitle') || 'Titre gauche'}
            </Text>
            <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
              {LANGUAGES.map((lang) => (
                <Col span={8} key={`left-${lang}`}>
                  <Input
                    addonBefore={lang.toUpperCase()}
                    value={safeConfig[`leftTitle_${lang}` as keyof AssociationConfig] as string || ''}
                    onChange={(e) => {
                      const key = `leftTitle_${lang}` as keyof AssociationConfig;
                      updateConfig((prev) => ({ ...prev, [key]: e.target.value }));
                    }}
                    placeholder={t('associationLesson.admin.titlePlaceholder', { lang: lang.toUpperCase() }) || `Title ${lang.toUpperCase()}...`}
                  />
                </Col>
              ))}
            </Row>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              {t('associationLesson.admin.rightTitle') || 'Titre droite'}
            </Text>
            <Row gutter={[8, 8]}>
              {LANGUAGES.map((lang) => (
                <Col span={8} key={`right-${lang}`}>
                  <Input
                    addonBefore={lang.toUpperCase()}
                    value={safeConfig[`rightTitle_${lang}` as keyof AssociationConfig] as string || ''}
                    onChange={(e) => {
                      const key = `rightTitle_${lang}` as keyof AssociationConfig;
                      updateConfig((prev) => ({ ...prev, [key]: e.target.value }));
                    }}
                    placeholder={t('associationLesson.admin.titlePlaceholder', { lang: lang.toUpperCase() }) || `Title ${lang.toUpperCase()}...`}
                  />
                </Col>
              ))}
            </Row>
          </div>
          <Flex gap={16} align="stretch" wrap="wrap">
            <div style={{ flex: '1 1 400px', minWidth: 0, maxWidth: '100%' }}>{listRenderer('left')}</div>
            <div style={{ flex: '1 1 400px', minWidth: 0, maxWidth: '100%' }}>{listRenderer('right')}</div>
          </Flex>
          <Text type="secondary">
            {t('associationLesson.admin.helper') ||
              'Utilisez la même clé pour les éléments correspondants. Les images sont optionnelles.'}
          </Text>
        </Flex>
      </Card>
      {/* JSON Editor Modal */}
      <Modal
        open={jsonOpen}
        onCancel={() => setJsonOpen(false)}
        footer={null}
        width={900}
        destroyOnClose
      >
        <JsonEditor<AssociationConfig>
          title={t('associationLesson.admin.jsonTitle') || 'JSON de configuration'}
          value={safeConfig}
          showStatement
          initialStatement={(initialStatement ?? safeConfig.statement) ?? ''}
          onStatementChange={(val) => {
            updateConfig((prev) => ({ ...prev, statement: val }));
            onStatementChange?.(val);
          }}
          onApply={(data) => {
            onConfigChange({ ...createEmptyAssociationConfig(), ...data });
            if (data?.statement !== undefined) {
              onStatementChange?.(data.statement || '');
            }
            setJsonOpen(false);
          }}
          minRows={16}
        />
      </Modal>
      <Modal
        title={t('associationLesson.admin.chooseImage') || 'Choisir une image'}
        open={mediaPickerOpen}
        onCancel={() => setMediaPickerOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
      >
        {mediaLoading ? (
          <Typography.Text>{t('associationLesson.admin.loading') || 'Chargement...'}</Typography.Text>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Input.Search
              placeholder={t('associationLesson.admin.searchPlaceholder') || 'Rechercher par nom de fichier'}
              allowClear
              value={mediaSearch}
              onChange={(e) => setMediaSearch(e.target.value)}
            />
            <List
              dataSource={filteredImages}
              rowKey={(item) => String(item.id)}
              renderItem={(item) => (
                <List.Item onClick={() => chooseMedia(item.file_path)} style={{ cursor: 'pointer' }}>
                  <Space>
                    <img src={item.file_path} alt={item.file_name} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4 }} />
                    <span>{item.file_name}</span>
                  </Space>
                </List.Item>
              )}
            />
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default AssociationLessonAdmin;
